import { Chess, type Square } from 'chess.js';
import { normalizeUci } from '../utils/chessHelpers';
import type { Color, Puzzle } from '../../types';

export interface PuzzleStart {
  fen: string; // position après le premier coup (adverse) de `moves`, où le joueur commence à répondre
  playerColor: Color;
  lastMoveSquares: { from: string; to: string };
}

/**
 * Position de départ effective d'un puzzle. Convention Lichess : `puzzle.fen`
 * précède le premier coup de `puzzle.moves`, qui est joué par le camp qui a
 * le trait dans cette position (celui qui vient de « se faire piéger » —
 * l'adversaire du point de vue du solveur) — toujours appliqué
 * automatiquement, jamais proposé à deviner.
 */
export function startPosition(puzzle: Puzzle): PuzzleStart {
  const chess = new Chess(puzzle.fen);
  const first = puzzle.moves[0];
  const from = first.slice(0, 2);
  const to = first.slice(2, 4);
  const promotion = first.length > 4 ? first.slice(4, 5) : undefined;
  chess.move({ from: from as Square, to: to as Square, promotion });
  return {
    fen: chess.fen(),
    playerColor: chess.turn(),
    lastMoveSquares: { from, to },
  };
}

export interface PuzzleMoveResult {
  correct: boolean;
  done: boolean; // résolu (dernier coup de la solution atteint)
  fen: string; // position à afficher (après la réponse adverse auto-jouée si correct et non terminé)
  opponentReplySquares?: { from: string; to: string };
}

/**
 * Vérifie le coup tenté à l'étape `stepIndex` de `puzzle.moves` (indices
 * impairs : 1, 3, 5… — les indices pairs sont les réponses adverses,
 * toujours auto-jouées, jamais à deviner). Si correct et qu'il reste une
 * réponse adverse, elle est rejouée automatiquement dans le résultat.
 *
 * Un coup qui fait échec et mat au dernier pas est accepté même s'il diffère
 * du mat de référence (plusieurs mats sont parfois possibles) — en dehors de
 * ce cas, exige le coup exact de la solution : contrairement aux exercices
 * tirés d'une partie, un puzzle n'a pas de moteur pour juger une alternative
 * « suffisamment bonne ».
 */
export function checkPuzzleMove(
  puzzle: Puzzle,
  fenBeforeMove: string,
  playedUci: string,
  stepIndex: number,
): PuzzleMoveResult | null {
  const chess = new Chess(fenBeforeMove);
  const from = playedUci.slice(0, 2);
  const to = playedUci.slice(2, 4);
  const promotion = playedUci.length > 4 ? playedUci.slice(4, 5) : undefined;
  let move;
  try {
    move = chess.move({ from: from as Square, to: to as Square, promotion });
  } catch {
    move = null;
  }
  if (!move) return null;

  const expectedUci = puzzle.moves[stepIndex];
  const isLastStep = stepIndex === puzzle.moves.length - 1;
  const correct = normalizeUci(playedUci) === normalizeUci(expectedUci) || (isLastStep && chess.isCheckmate());

  if (!correct) {
    return { correct: false, done: false, fen: chess.fen() };
  }
  if (isLastStep) {
    return { correct: true, done: true, fen: chess.fen() };
  }

  const replyUci = puzzle.moves[stepIndex + 1];
  const rFrom = replyUci.slice(0, 2);
  const rTo = replyUci.slice(2, 4);
  const rPromotion = replyUci.length > 4 ? replyUci.slice(4, 5) : undefined;
  chess.move({ from: rFrom as Square, to: rTo as Square, promotion: rPromotion });

  return {
    correct: true,
    done: false,
    fen: chess.fen(),
    opponentReplySquares: { from: rFrom, to: rTo },
  };
}
